import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamAPI, rolesAPI, groupsAPI, adminAPI, chatAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { canAdmin, PERMISSION_LEVELS, getPermissionLabel } from '../utils/permissions';
import Navbar from '../components/Navbar';
import DeleteButton from '../components/ui/DeleteButton';
import { Plus, Edit, Mail, Users as UsersIcon, Settings, Search, Trash2, Shield, RotateCcw, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import { useModalHistory } from '../hooks/useModalHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getPhotoUrl = (picture) => {
  if (!picture) return null;
  if (picture.startsWith('data:')) return picture;
  if (picture.startsWith('/api/uploads/')) return `${BACKEND_URL}${picture}`;
  return picture;
};

const ACCOUNT_STATUS_OPTIONS = [
  { value: 'registered', label: 'Registered' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'active', label: 'Active' },
];

const People = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isPersonDialogOpen, setIsPersonDialogOpen] = useState(false);
  const personModal = useModalHistory(isPersonDialogOpen, setIsPersonDialogOpen);
  // Reset form whenever dialog closes (covers back-button close too)
  useEffect(() => { if (!isPersonDialogOpen) resetPersonForm(); }, [isPersonDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [editingPerson, setEditingPerson] = useState(null);
  const [personFormData, setPersonFormData] = useState({ name: '', email: '', roles: [], permission_level: 'viewer', account_status: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  const isAdmin = canAdmin(user);

  const PAGE_SIZE = 20;
  const sentinelRef = useRef(null);
  const skipRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(false);
  const filterRef = useRef({ search: '', group: 'all' });
  const searchDebounceRef = useRef(null);
  const mountedRef = useRef(false);

  // Keep filterRef in sync
  filterRef.current = { search: searchQuery, group: groupFilter, sortBy };

  const loadMeta = useCallback(async () => {
    try {
      const [rolesRes, groupsRes] = await Promise.all([rolesAPI.getAll(), groupsAPI.getAll()]);
      setRoles(rolesRes.data);
      setGroups(groupsRes.data);
    } catch {}
  }, []);

  const loadPeople = useCallback(async (reset = false) => {
    if (loadingRef.current) return;
    if (!reset && !hasMoreRef.current) return;

    loadingRef.current = true;
    const currentSkip = reset ? 0 : skipRef.current;
    if (reset) { setLoading(true); skipRef.current = 0; }
    else setLoadingMore(true);

    try {
      const { search, group, sortBy: sort_by } = filterRef.current;
      const res = await teamAPI.getAll({ skip: currentSkip, limit: PAGE_SIZE, search, group, sort_by });
      const { members: items, has_more: more } = res.data;

      if (reset) setPeople(items);
      else setPeople(prev => [...prev, ...items]);

      skipRef.current = currentSkip + items.length;
      hasMoreRef.current = more;
      setHasMore(more);
    } catch {
      toast.error('Failed to load team members');
    } finally {
      loadingRef.current = false;
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  }, []);

  // Initial load — set mountedRef AFTER kicking off initial load
  useEffect(() => {
    loadMeta();
    loadPeople(true);
    // Mark mounted after first render so filter-change effect skips mount
    const t = setTimeout(() => { mountedRef.current = true; }, 0);
    return () => clearTimeout(t);
  }, [loadMeta, loadPeople]);

  // Reload on filter changes (debounced)
  useEffect(() => {
    if (!mountedRef.current) return;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => { loadPeople(true); }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery, groupFilter, sortBy, loadPeople]);

  // Infinite scroll sentinel — re-run when `loading` flips so that if the
  // first page doesn't fill the screen the observer fires immediately.
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMoreRef.current && !loadingRef.current) {
        loadPeople(false);
      }
    }, { rootMargin: '200px' });
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadPeople, loading]);

  const rolesByGroup = (roles || []).reduce((acc, role) => {
    if (!acc[role.group]) acc[role.group] = [];
    acc[role.group].push(role);
    return acc;
  }, {});

  const handlePersonSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPerson) {
        const updateData = { ...personFormData };
        await teamAPI.update(editingPerson.member_id, updateData);
        toast.success('Person updated');
        // Refresh auth context if editing self
        if (editingPerson.email === user?.email) {
          await checkAuth();
        }
      } else {
        const { account_status, ...createData } = personFormData;
        await teamAPI.create(createData);
        toast.success('Person added. Initial password is their email address.');
      }
      personModal.close();
      loadPeople(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save person');
    }
  };

  const handleEditPerson = (person) => {
    setEditingPerson(person);
    setPersonFormData({ name: person.name, email: person.email, roles: person.roles || [], permission_level: person.permission_level || 'viewer', account_status: person.account_status || null });
    personModal.open();
  };

  const handleDeletePerson = async (personId) => {
    try {
      await teamAPI.delete(personId);
      toast.success('Person removed');
      loadPeople(true);
    } catch (error) {
      toast.error('Failed to remove person');
    }
  };

  const resetPersonForm = () => {
    setPersonFormData({ name: '', email: '', roles: [], permission_level: 'viewer', account_status: null });
    setEditingPerson(null);
  };

  const handleResetAccount = async (person) => {
    if (window.confirm(`Reset account for ${person.name}? Their password will be reset to their email address.`)) {
      try {
        await adminAPI.resetAccount(person.member_id);
        toast.success('Account reset. Password is now their email address.');
        personModal.close();
        loadPeople(true);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to reset account');
      }
    }
  };

  const handleStartChat = async (person) => {
    if (person.email === user?.email) {
      toast.error("You can't message yourself.");
      return;
    }
    try {
      const res = await chatAPI.startChatByEmail(person.email);
      navigate(`/chat/${res.data.chat_id}`);
    } catch {
      toast.error('Unable to start chat. The member may not have an account yet.');
    }
  };

  const addRoleToPerson = (roleName) => {
    if (roleName && !personFormData.roles.includes(roleName)) {
      setPersonFormData({ ...personFormData, roles: [...personFormData.roles, roleName] });
    }
  };

  const removeRoleFromPerson = (role) => {
    setPersonFormData({ ...personFormData, roles: personFormData.roles.filter((r) => r !== role) });
  };

  return (
    <div className="bg-slate-50 pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900">People</h1>
            <p className="mt-1 text-sm sm:text-base text-slate-600">Manage your team members</p>
          </div>
          {isAdmin && (
            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/roles')}
                  data-testid="edit-roles-btn"
                  className="h-9 sm:h-10 px-4 sm:px-6 rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-all duration-200 shadow-sm font-medium flex items-center gap-2 text-sm"
                >
                  <Settings className="w-4 h-4" />
                  Edit Roles
                </button>
              <button
                onClick={() => personModal.open()}
                data-testid="add-person-btn"
                className="h-9 sm:h-10 px-4 sm:px-6 rounded-full bg-green-700 text-white hover:bg-green-800 transition-all duration-200 shadow-sm font-medium flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Person
              </button>
            </div>
          )}
        </div>

        {/* Search & Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-people-input"
              className="pl-10 bg-white h-10"
            />
          </div>
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger data-testid="group-filter-select" className="w-full sm:w-48 bg-white h-10">
              <SelectValue placeholder="All Groups" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              {(groups || []).map((g) => (
                <SelectItem key={g.group_id} value={g.name}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Sort toggle */}
          <div className="flex h-10 bg-white border border-slate-200 rounded-lg p-1 gap-1 flex-shrink-0">
            <button
              onClick={() => setSortBy('name')}
              data-testid="sort-az-btn"
              className={`px-3 rounded-md text-sm font-medium transition-colors ${sortBy === 'name' ? 'bg-green-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >A-Z</button>
            <button
              onClick={() => setSortBy('updated')}
              data-testid="sort-recent-btn"
              className={`px-3 rounded-md text-sm font-medium transition-colors ${sortBy === 'updated' ? 'bg-green-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >Recent</button>
          </div>
        </div>

        {/* People List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700 mx-auto"></div>
          </div>
        ) : people.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-xl">
            <UsersIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{(searchQuery || groupFilter !== 'all') ? 'No people match your filters' : 'No people yet'}</p>
            {isAdmin && !searchQuery && groupFilter === 'all' && (
              <button
                onClick={() => personModal.open()}
                className="mt-3 text-sm text-green-700 hover:text-green-800 font-medium"
              >
                Add your first person
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 lg:gap-6">
            {people.map((person) => (
              <div
                key={person.member_id}
                data-testid={`person-card-${person.member_id}`}
                className={`bg-white rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-all duration-200 border-2 ${
                  person.account_status === 'active' ? 'border-emerald-400' :
                  person.account_status === 'pending_approval' ? 'border-amber-300' :
                  'border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Avatar */}
                    {getPhotoUrl(person.picture) ? (
                      <img
                        src={getPhotoUrl(person.picture)}
                        alt={person.name}
                        className="w-[52px] h-[52px] sm:w-[75px] sm:h-[75px] rounded-full object-cover flex-shrink-0 ring-2 ring-slate-100"
                      />
                    ) : (
                      <div className="w-[52px] h-[52px] sm:w-[75px] sm:h-[75px] rounded-full bg-green-700 flex items-center justify-center flex-shrink-0 ring-2 ring-slate-100">
                        <span className="text-white text-sm font-semibold">
                          {person.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-slate-900 break-words">{person.name}</h3>
                      <p className="text-xs sm:text-sm text-slate-500 flex items-center gap-1.5 mt-0.5">
                        <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="break-all">{person.email}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-2">
                    {person.email !== user?.email && (
                      <button
                        onClick={() => handleStartChat(person)}
                        data-testid={`chat-person-${person.member_id}`}
                        className="p-2 text-slate-400 hover:text-green-700 hover:bg-slate-50 rounded-lg transition-colors"
                        title="Message"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => handleEditPerson(person)}
                        data-testid={`edit-person-${person.member_id}`}
                        className="p-2 text-slate-400 hover:text-green-700 hover:bg-slate-50 rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-[10px] sm:text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  {getPermissionLabel(person.permission_level || 'viewer')}
                </p>
                {person.account_status && person.account_status !== 'active' && (
                  <span data-testid={`status-badge-${person.member_id}`} className={`inline-flex items-center mt-1.5 text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded-full ${
                    person.account_status === 'pending_approval' ? 'bg-amber-50 text-amber-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {person.account_status === 'pending_approval' ? 'Pending Approval' : 'Registered'}
                  </span>
                )}
                {person.roles && person.roles.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {person.roles.map((role) => (
                      <Badge key={role} variant="outline" className="text-[11px] sm:text-xs px-2 py-0.5">{role}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel — always in DOM so IntersectionObserver works from mount */}
        <div ref={sentinelRef} className="h-1 mt-2" />
        {loadingMore && (
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-700 mx-auto" />
          </div>
        )}

        {/* Add/Edit Person Dialog */}
        <Dialog open={isPersonDialogOpen} onOpenChange={personModal.onOpenChange}>
          <DialogContent className="bg-white max-w-lg sm:max-w-2xl mx-4 sm:mx-auto max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPerson ? 'Edit Person' : 'Add Person'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePersonSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="person-name-input"
                    value={personFormData.name}
                    onChange={(e) => setPersonFormData({ ...personFormData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="person-email-input"
                    value={personFormData.email}
                    onChange={(e) => setPersonFormData({ ...personFormData, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Roles</Label>
                <Select onValueChange={addRoleToPerson}>
                  <SelectTrigger className="mt-2" data-testid="role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {(groups || []).map((group) => {
                      const groupRoles = rolesByGroup[group.name] || [];
                      if (groupRoles.length === 0) return null;
                      return (
                        <SelectGroup key={group.group_id}>
                          <SelectLabel>{group.name}</SelectLabel>
                          {groupRoles.map((role) => (
                            <SelectItem key={role.role_id} value={role.name}>{role.name}</SelectItem>
                          ))}
                        </SelectGroup>
                      );
                    })}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {personFormData.roles.map((role) => (
                    <Badge key={role} variant="secondary" className="flex items-center gap-1.5 text-xs">
                      {role}
                      <button type="button" onClick={() => removeRoleFromPerson(role)} className="text-slate-500 hover:text-slate-700 leading-none">x</button>
                    </Badge>
                  ))}
                </div>
              </div>
              {isAdmin && (
                <div>
                  <Label>Permission Level</Label>
                  <Select
                    value={personFormData.permission_level}
                    onValueChange={(val) => setPersonFormData({ ...personFormData, permission_level: val })}
                  >
                    <SelectTrigger className="mt-2" data-testid="permission-level-select">
                      <SelectValue placeholder="Select permission level" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {PERMISSION_LEVELS.filter((p) => p.value !== 'root').map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          <div>
                            <span className="font-medium">{p.label}</span>
                            <span className="text-xs text-slate-500 ml-2 hidden sm:inline">{p.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isAdmin && editingPerson && (
                <div>
                  <Label>Account Status</Label>
                  <Select
                    value={personFormData.account_status || 'registered'}
                    onValueChange={(val) => setPersonFormData({ ...personFormData, account_status: val })}
                  >
                    <SelectTrigger className="mt-2" data-testid="account-status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {ACCOUNT_STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button type="submit" data-testid="submit-person-btn" className="w-full">
                {editingPerson ? 'Update Person' : 'Add Person'}
              </Button>
              {editingPerson && isAdmin && (
                <button
                  type="button"
                  onClick={() => handleResetAccount(editingPerson)}
                  data-testid="reset-account-btn"
                  className="w-full flex items-center justify-center gap-2 text-sm font-medium text-amber-700 hover:bg-amber-50 border border-amber-200 px-4 py-2.5 rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset Account
                </button>
              )}
              {editingPerson && isAdmin && (
                <DeleteButton
                  onConfirm={() => {
                    handleDeletePerson(editingPerson.member_id);
                    personModal.close();
                  }}
                  className="w-full flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium border border-rose-200 hover:border-rose-400"
                  title="Remove Person"
                  testId={`delete-person-${editingPerson.member_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Person
                </DeleteButton>
              )}
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default People;
